import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, Video } from 'lucide-react';
import { schedulesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format, parseISO, isToday, isTomorrow, addDays, startOfDay } from 'date-fns';
import { useIsMobile } from '../hooks/useIsMobile';

// Countdown box component matching customer portal style
const CountdownBox = ({ value, label }) => (
  <div className="flex items-center gap-1">
    <span
      style={{
        background: 'linear-gradient(145deg, var(--highlight-15) 0%, var(--highlight-8) 100%)',
        border: '1px solid var(--border-muted)',
        padding: '6px 10px',
        borderRadius: 8,
        fontSize: 16,
        fontWeight: 700,
        color: 'var(--text-primary)',
        fontFamily: "'JetBrains Mono', monospace",
        minWidth: 40,
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      {String(value).padStart(2, '0')}
    </span>
    <span
      style={{
        fontSize: 11,
        fontWeight: 500,
        color: 'var(--text-muted)',
        marginLeft: 2,
      }}
    >
      {label}
    </span>
  </div>
);

export default function NextVideoCountdown() {
  const { studio, isAuthenticated } = useAuth();
  const studioId = studio?.id;
  const [currentTime, setCurrentTime] = useState(new Date());
  const isMobile = useIsMobile();

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch upcoming schedules (next 7 days)
  const { data: schedulesData } = useQuery({
    queryKey: ['upcoming-schedules-countdown', studioId],
    queryFn: async () => {
      const today = new Date();
      const endDate = addDays(today, 7);
      return schedulesAPI.getSchedules(studioId, {
        startDate: format(today, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
        isActive: 'true'
      });
    },
    enabled: !!studioId && isAuthenticated,
    refetchInterval: false,
  });

  // Find the next upcoming schedule
  const nextSchedule = useMemo(() => {
    if (!schedulesData?.data?.schedules || !studioId) return null;

    const now = currentTime;
    const schedules = schedulesData.data.schedules;
    let nextSched = null;
    let nextSchedTime = null;

    schedules.forEach(schedule => {
      let scheduleDateTime = null;

      if (schedule.specificDate) {
        const date = parseISO(schedule.specificDate);
        const [hours, minutes] = schedule.startTime.split(':');
        scheduleDateTime = new Date(date);
        scheduleDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      } else if (schedule.dayOfWeek !== null && schedule.dayOfWeek !== undefined) {
        const today = new Date(now);
        const currentDayOfWeek = today.getDay();
        let daysUntilNext = schedule.dayOfWeek - currentDayOfWeek;

        const [hours, minutes] = schedule.startTime.split(':');
        const todayAtScheduleTime = new Date(today);
        todayAtScheduleTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

        if (daysUntilNext < 0 || (daysUntilNext === 0 && todayAtScheduleTime <= now)) {
          daysUntilNext += 7;
        }

        scheduleDateTime = addDays(startOfDay(now), daysUntilNext);
        scheduleDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      }

      if (scheduleDateTime && scheduleDateTime > now) {
        if (!nextSchedTime || scheduleDateTime < nextSchedTime) {
          nextSchedTime = scheduleDateTime;
          nextSched = schedule;
        }
      }
    });

    return nextSched ? { schedule: nextSched, time: nextSchedTime } : null;
  }, [schedulesData, currentTime, studioId]);

  // Calculate countdown
  const countdown = useMemo(() => {
    if (!nextSchedule || !nextSchedule.time) return null;

    const diff = nextSchedule.time.getTime() - currentTime.getTime();

    if (diff <= 0) return null;

    const totalSeconds = Math.floor(diff / 1000);
    const totalHours = Math.floor(totalSeconds / 3600);
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { days, hours, minutes, seconds, totalSeconds };
  }, [nextSchedule, currentTime]);

  // Format the schedule time display
  const scheduleTimeDisplay = useMemo(() => {
    if (!nextSchedule || !nextSchedule.time) return null;

    const scheduleTime = nextSchedule.time;
    const schedule = nextSchedule.schedule;

    let dateText = '';
    if (isToday(scheduleTime)) {
      dateText = 'Today';
    } else if (isTomorrow(scheduleTime)) {
      dateText = 'Tomorrow';
    } else {
      dateText = format(scheduleTime, 'EEEE, MMM d');
    }

    const timeText = format(scheduleTime, 'h:mm a');
    const videoTitle = schedule.classType === 'live'
      ? (schedule.liveTitle || 'Live Class')
      : (schedule.video?.title || 'Scheduled Class');
    const isLive = schedule.classType === 'live';

    return { dateText, timeText, videoTitle, isLive };
  }, [nextSchedule]);

  // Early return AFTER all hooks
  if (!isAuthenticated) {
    return null;
  }

  const displayCountdown = countdown || { days: 0, hours: 0, minutes: 0, seconds: 0 };
  const isLoading = !schedulesData && !!studioId;

  // Determine color scheme based on class type
  const isLive = scheduleTimeDisplay?.isLive;
  const colorVar = isLive ? 'primary' : 'secondary';

  return (
    <div
      style={{
        margin: isMobile ? '12px 16px' : '16px 24px 20px',
        padding: isMobile ? '14px 16px' : '16px 24px',
        background: `linear-gradient(135deg, var(--${colorVar}-15) 0%, var(--${colorVar}-8) 50%, var(--bg-surface) 100%)`,
        borderRadius: 16,
        border: `1px solid var(--${colorVar}-20)`,
        boxShadow: `0 4px 20px rgba(0,0,0,0.1), 0 0 40px var(--${colorVar}-10)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top shine effect */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background: `linear-gradient(90deg, transparent, var(--${colorVar}-30), var(--highlight-15), var(--${colorVar}-30), transparent)`,
        }}
      />

      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* Left side - Video info */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4" style={{ color: 'var(--primary)' }} />
              <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Loading...</span>
            </div>
          ) : nextSchedule && scheduleTimeDisplay ? (
            <>
              {/* Type emblem with text */}
              <div
                style={{
                  padding: '8px 12px',
                  borderRadius: 10,
                  background: scheduleTimeDisplay.isLive
                    ? 'linear-gradient(135deg, var(--primary), var(--primary-hover))'
                    : 'linear-gradient(135deg, var(--secondary), var(--secondary-hover))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: scheduleTimeDisplay.isLive
                    ? '0 4px 12px var(--primary-30)'
                    : '0 4px 12px var(--secondary-30)',
                  flexShrink: 0,
                }}
              >
                <span style={{
                  color: 'var(--bg-base)',
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  {scheduleTimeDisplay.isLive ? 'Live' : 'Video'}
                </span>
              </div>

              {/* Text content */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 500 }}>
                    Next Class:
                  </span>
                  <span
                    style={{
                      color: 'var(--text-primary)',
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                    className="truncate"
                  >
                    {scheduleTimeDisplay.videoTitle}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Clock className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                  <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                    {scheduleTimeDisplay.dateText} {scheduleTimeDisplay.timeText}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
              <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>No upcoming classes</span>
            </div>
          )}
        </div>

        {/* Right side - Countdown */}
        <div className="flex items-center gap-2">
          {displayCountdown.days > 0 && (
            <>
              <CountdownBox value={displayCountdown.days} label="day" />
              <span style={{ color: 'var(--text-muted)', fontSize: 16, fontWeight: 600 }}>:</span>
            </>
          )}
          <CountdownBox value={displayCountdown.hours} label="hr" />
          <span style={{ color: 'var(--text-muted)', fontSize: 16, fontWeight: 600 }}>:</span>
          <CountdownBox value={displayCountdown.minutes} label="min" />
          {displayCountdown.days === 0 && (
            <>
              <span style={{ color: 'var(--text-muted)', fontSize: 16, fontWeight: 600 }}>:</span>
              <CountdownBox value={displayCountdown.seconds} label="sec" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
